import React, { useMemo, useState } from "react";
import { calls as callsApi } from "../api/endpoints";
import { DataTableShell, EmptyState, MetricStrip, PageHeader, SectionCard } from "../components/Workspace";
import type { CallImportSummary } from "../types";

const EXPECTED_COLUMNS = [
  "HotelCode",
  "HotelName",
  "SalesStaffEmail",
  "ShoppersEmail",
  "CallType",
  "ServiceLevel",
  "Scenario",
  "ScheduledMonth",
  "ScheduledDate",
  "Earliest",
  "HMC",
  "ContractYear",
  "OrderID",
  "InvoiceID",
  "Language",
];

function statusTone(status: string) {
  if (status === "imported") return "tone-success";
  if (status === "ready") return "tone-brand";
  return "tone-danger";
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`badge-pill ${statusTone(status)}`.trim()}>
      {status === "ready" ? "Ready" : status === "imported" ? "Imported" : "Needs review"}
    </span>
  );
}

export default function ImportedCallsPage() {
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<CallImportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; message: string } | null>(null);

  const metricItems = useMemo(() => {
    if (!summary) {
      return [
        { label: "Rows", value: 0, detail: "Upload a file to validate", tone: "neutral" as const },
        { label: "Ready", value: 0, detail: "Rows that can import cleanly", tone: "brand" as const },
        { label: "Warnings", value: 0, detail: "Rows with non-blocking notes", tone: "warning" as const },
        { label: "Issues", value: 0, detail: "Rows that need changes", tone: "danger" as const },
      ];
    }
    return [
      { label: "Rows", value: summary.total_rows, detail: summary.file_name, tone: "neutral" as const },
      { label: "Ready", value: summary.ready_rows, detail: "Can be imported now", tone: "brand" as const },
      { label: "Warnings", value: summary.warning_rows, detail: "Will still import", tone: "warning" as const },
      {
        label: importing || summary.imported_rows > 0 ? "Imported" : "Issues",
        value: importing || summary.imported_rows > 0 ? summary.imported_rows : summary.invalid_rows,
        detail: importing || summary.imported_rows > 0 ? "Rows created in calls" : "Rows blocked by validation",
        tone: importing || summary.imported_rows > 0 ? ("success" as const) : ("danger" as const),
      },
    ];
  }, [summary, importing]);

  const canImport = Boolean(file && summary && summary.ready_rows > 0 && !loading && !importing);

  async function handlePreview() {
    if (!file) {
      setNotice({ tone: "danger", message: "Choose a .csv, .xls, or .xlsx file first." });
      return;
    }
    setLoading(true);
    setNotice(null);
    try {
      const result = await callsApi.importPreview(file);
      setSummary(result);
      setNotice({ tone: "success", message: result.message });
    } catch (error) {
      setSummary(null);
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Preview failed." });
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    if (!file) return;
    setImporting(true);
    setNotice(null);
    try {
      const result = await callsApi.importCommit(file);
      setSummary(result);
      setNotice({
        tone: result.failed_rows > 0 ? "danger" : "success",
        message: result.message,
      });
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Import failed." });
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Imported Calls"
        title="Import Call Details"
        description="Upload a call-details spreadsheet, validate every row, and import only the rows that resolve cleanly."
        compact
      />

      <MetricStrip items={metricItems} />

      <SectionCard
        title="Upload File"
        subtitle="The importer accepts .csv, .xls, and .xlsx files. Review the validation table before committing any rows."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={handlePreview} className="button-secondary" disabled={!file || loading || importing}>
              {loading ? "Validating..." : "Validate file"}
            </button>
            <button type="button" onClick={handleImport} className="button-primary" disabled={!canImport}>
              {importing ? "Importing..." : "Import ready rows"}
            </button>
          </div>
        }
      >
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr),minmax(0,0.8fr)]">
          <div className="space-y-3">
            <input
              type="file"
              accept=".csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              onChange={(event) => {
                setFile(event.target.files?.[0] || null);
                setSummary(null);
                setNotice(null);
              }}
              className="input-shell !py-3"
            />
            <div className="text-sm text-slate-500">
              Use the legacy-style columns and keep the header row intact. Existing matching calls are blocked as duplicates.
            </div>
            {notice ? (
              <div
                className={`rounded-2xl border px-4 py-3 text-sm ${
                  notice.tone === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-rose-200 bg-rose-50 text-rose-800"
                }`.trim()}
              >
                {notice.message}
              </div>
            ) : null}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-slate-50/70 p-4">
            <div className="text-sm font-semibold text-slate-900">Expected Columns</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {EXPECTED_COLUMNS.map((column) => (
                <span key={column} className="badge-pill tone-neutral">
                  {column}
                </span>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      <DataTableShell
        title="Validation Results"
        subtitle="Rows marked ready can import now. Errors are blocking. Warnings are informational."
      >
        {summary && summary.rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Status</th>
                  <th>Hotel</th>
                  <th>Sales Staff</th>
                  <th>Shopper</th>
                  <th>Type / Service</th>
                  <th>Scheduled</th>
                  <th>Messages</th>
                </tr>
              </thead>
              <tbody>
                {summary.rows.map((row) => (
                  <tr key={row.row_number}>
                    <td className="font-mono text-sm font-semibold text-slate-900">{row.row_number}</td>
                    <td>
                      <div className="space-y-2">
                        <StatusPill status={row.status} />
                        {row.imported_call_id ? (
                          <div className="text-xs text-emerald-700">Call #{row.imported_call_id}</div>
                        ) : null}
                        {row.duplicate_existing_call_id ? (
                          <div className="text-xs text-amber-700">Duplicate #{row.duplicate_existing_call_id}</div>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <div className="font-semibold text-slate-900">{row.hotel_code || "No code"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.resolved_property_name || row.hotel_name || "Unresolved hotel"}</div>
                      {row.resolved_hmc_name ? (
                        <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">{row.resolved_hmc_name}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.resolved_sales_staff_name || "Unassigned"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.sales_staff_email || "No email"}</div>
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.resolved_shopper_name || "Unassigned"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.shopper_email || "No email"}</div>
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.call_type || "Missing type"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.service_level || "Missing service level"}</div>
                      {row.resolved_language_name ? (
                        <div className="mt-1 text-xs text-slate-500">Language: {row.resolved_language_name}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.scheduled_date || "Missing date"}</div>
                      {row.scenario ? <div className="mt-1 text-sm text-slate-600">{row.scenario}</div> : null}
                      {row.resolved_order_id ? (
                        <div className="mt-1 text-xs text-slate-500">Order #{row.resolved_order_id}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="space-y-2">
                        {row.errors.map((message) => (
                          <div key={`error-${row.row_number}-${message}`} className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-800">
                            {message}
                          </div>
                        ))}
                        {row.warnings.map((message) => (
                          <div key={`warning-${row.row_number}-${message}`} className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            {message}
                          </div>
                        ))}
                        {row.errors.length === 0 && row.warnings.length === 0 ? (
                          <div className="text-sm text-slate-500">No issues.</div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No validation yet"
            message="Upload a call-details file and run validation to see row-level feedback before importing."
          />
        )}
      </DataTableShell>
    </div>
  );
}
